import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { PartnersUtilsService } from '@app/modules/partners/partners-utils.service';
import { ServiceDataFields } from '@app/modules/service-data/components/service-data/service-data-form/field-sets/service-data-fields.enum';
import { ParentingTestType } from '@app/modules/service-data/types/paternity.types';
import {
  AnalysisType,
  Bundle,
  IncidentalFindingsReporting,
  Partner,
} from '@core/core.types';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { ProductsListState } from '@core/store/products/products.state';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { Store } from '@ngxs/store';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { controlContainerProvider } from '@shared/helpers';
import {
  combineLatest,
  first,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

@Component({
  selector: 'app-service-data-form-bundle-information',
  imports: [
    ReactiveFormsModule,
    NgbTooltip,
    AsyncPipe,
    TranslateModule,
    ValidationErrorComponent,
    DataTableComponent,
    SelectComponent,
    SelectOptionComponent,
    RequirePermissionDirective,
  ],
  templateUrl: './service-data-form-bundle-information.component.html',
  viewProviders: [controlContainerProvider],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceDataFormBundleInformationComponent implements OnInit {
  private controlContainer = inject(ControlContainer);
  private partnersUtilsService = inject(PartnersUtilsService);
  private authService = inject(AccountUtilsService);
  private store = inject(Store);
  protected bundles$: Observable<Bundle[]>;
  protected dataTableConfiguration: DataTableConfiguration;
  protected bundleFormControl = new FormControl<Bundle>(
    { value: null, disabled: true },
    [Validators.required]
  );
  @Input({ required: true }) fieldSet: string[] = [];
  @Input() id: string;

  private rows: Observable<Bundle[]>;

  protected get form() {
    return this.controlContainer.control as FormGroup;
  }
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.bundles$ = this.getBundles();

    this.rows = this.form.get('bundleIds').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.form.get('bundleIds').value),
      switchMap((bundleIds) =>
        this.store
          .select(ProductsListState.getBundles)
          .pipe(map((getBundles) => getBundles(bundleIds)))
      )
    );

    this.dataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      tableHeadClasses: '',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'serviceData.form.tooltips.unassignBundleFromServiceData',
          action: this.unassignBundle.bind(this),
          icon: 'fa-light fa-xmark',
          type: DataTableActionType.BUTTON,
          permission: this.id ? 'medicalInformation' : undefined,
          disabled: () => this.form.get('bundleIds').disabled,
        },
      ],
      columns: [
        {
          label: 'Bundle name',
          property: 'name',
        },
      ],
      rows$: this.rows,
    };
  }

  protected assignBundle(): void {
    if (!this.canManageBundles() || !this.bundleFormControl.valid) {
      return;
    }

    const bundleIdsControl = this.form.get('bundleIds');

    bundleIdsControl.patchValue([
      ...bundleIdsControl.value,
      this.bundleFormControl.value,
    ]);

    this.bundleFormControl.reset();
  }

  private unassignBundle(event: MouseEvent, bundle: Bundle) {
    event.preventDefault();

    if (!this.canManageBundles()) {
      return;
    }

    const bundleIdsControl = this.form.get('bundleIds');

    bundleIdsControl.patchValue(
      bundleIdsControl.value.filter(
        (bundleId: string) => bundleId !== bundle.id
      )
    );
  }

  private getBundles(): Observable<Bundle[]> {
    const partnerIdControl = this.form.get('partnerId');
    const bundleIdsControl = this.form.get('bundleIds');
    const canEditBundles$ = this.id
      ? this.authService.hasRequiredPermission$('medicalInformation')
      : of(true);

    return combineLatest([
      partnerIdControl.valueChanges.pipe(startWith(partnerIdControl.value)),
      bundleIdsControl.valueChanges.pipe(startWith([])),
      canEditBundles$,
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(([partnerId, bundleIds, canEditBundles]) =>
        this.partnersUtilsService.getPartnerById$(partnerId).pipe(
          first(),
          switchMap((partner: Partner) =>
            partner
              ? this.partnersUtilsService
                  .getPartnerBundles(partner.bundleConfigurations)
                  .pipe(
                    map((bundles: Bundle[]) =>
                      bundles.filter(
                        (bundle) =>
                          (bundleIds ?? []).findIndex(
                            (bundleId: string) => bundleId === bundle.id
                          ) < 0
                      )
                    ),
                    tap(() => {
                      if (
                        this.form.get('bundleIds').enabled &&
                        canEditBundles
                      ) {
                        this.bundleFormControl.enable();
                      } else {
                        this.bundleFormControl.disable();
                      }
                    })
                  )
              : of([]).pipe(tap(() => this.bundleFormControl.disable()))
          )
        )
      ),
      map((bundles) =>
        bundles.sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
        )
      )
    );
  }

  private canManageBundles(): boolean {
    return (
      !this.id || this.authService.hasRequiredPermission('medicalInformation')
    );
  }

  protected readonly ServiceDataFields = ServiceDataFields;
  protected readonly IncidentalFindingsReporting = IncidentalFindingsReporting;
  protected readonly AnalysisType = AnalysisType;
  protected readonly parentingTestTypes = Object.values(ParentingTestType);
}
